// The typed cad-api wrappers — the `api` map components call (never `fetch`
// directly). Extracted from api/client.ts (verbatim).
import type {
  AdminGrowth,
  AdminPlan,
  AdminTeamDetail,
  AdminTeamRow,
  AdminUserRow,
  AssignableRole,
  AuditEvent,
  BillingConfig,
  BillingInterval,
  Branch,
  Comment,
  CommentCreateIn,
  CommentPatchIn,
  CommentResolvedFilter,
  DiffBlobs,
  ExportFormat,
  ExportToken,
  MeStats,
  MergePreview,
  Model,
  ModelStatus,
  PendingVerification,
  Preferences,
  PreferencesOut,
  ResendVerification,
  ShareAccess,
  ShareCommentCreateIn,
  ShareCommentPatchIn,
  ShareJoin,
  ShareLink,
  ShareLinkSummary,
  ShareMeta,
  PlanLimitValue,
  ShareScope,
  Subscription,
  PrintTemplateRow,
  SyncTicketResponse,
  TeamDetail,
  TeamMemberRow,
  TeamSummary,
  User,
  Version,
  VersionGraph,
} from '../types';
import { API_BASE } from './constants';
import { ensureCsrf } from './csrf';
import { request, publicRequest } from './request';
import { loadDocBlob, saveDocBlob, snapshotDocBlob } from './docBlob';

export const oauthGoogleStartUrl = `${API_BASE}/auth/oauth/google/start`;

export const api = {
  csrf: ensureCsrf,

  // auth
  register: (body: { email: string; password: string; name?: string }) =>
    request<{ status: string; detail: string }>('/auth/register', { method: 'POST', body }),
  login: (body: { email: string; password: string }) =>
    request<User>('/auth/login', { method: 'POST', body }),
  logout: () => request<{ status: string }>('/auth/logout', { method: 'POST' }),
  me: () => request<User>('/auth/me'),
  // Signup verification. verifyEmail returns the SAME User login returns — on
  // success the server has issued the real session, so there is no second step.
  verifyEmail: (body: { code: string }) =>
    request<User>('/auth/verify-email', { method: 'POST', body }),
  resendVerification: () =>
    request<ResendVerification>('/auth/resend-verification', { method: 'POST' }),
  // Reads the address out of the httpOnly pending cookie so /verify-email can
  // redraw itself after a reload.
  pendingVerification: () => request<PendingVerification>('/auth/pending-verification'),
  // First-run: mark the one-time "choose a plan" step done. Idempotent; returns the
  // refreshed user so the SPA can land editor-first afterward.
  completeOnboarding: () => request<User>('/auth/onboarding/complete', { method: 'POST' }),
  forgotPassword: (body: { email: string }) =>
    request<{ status: string; detail: string }>('/auth/password/forgot', { method: 'POST', body }),
  resetPassword: (body: { token: string; password: string }) =>
    request<{ status: string }>('/auth/password/reset', { method: 'POST', body }),
  changePassword: (body: { current_password: string; new_password: string }) =>
    request<{ status: string }>('/auth/password/change', { method: 'POST', body }),

  // models
  listModels: (status: ModelStatus = 'active') => request<Model[]>('/models', { query: { status } }),
  getModel: (id: string) => request<Model>(`/models/${id}`),
  createModel: (body: { name: string; team_id?: string }) =>
    request<Model>('/models', { method: 'POST', body }),
  renameModel: (id: string, name: string) =>
    request<Model>(`/models/${id}`, { method: 'PATCH', body: { name } }),
  archiveModel: (id: string) => request<Model>(`/models/${id}/archive`, { method: 'POST' }),
  unarchiveModel: (id: string) => request<Model>(`/models/${id}/unarchive`, { method: 'POST' }),
  deleteModel: (id: string) => request<{ status: string }>(`/models/${id}`, { method: 'DELETE' }),
  exportModel: (id: string, format: ExportFormat) =>
    request<ExportToken>(`/models/${id}/export`, { method: 'POST', query: { format } }),

  // stats
  getStats: () => request<MeStats>('/me/stats'),

  // ── Custom print templates (paid; create returns 402 for free, rendered as the
  //    upgrade prompt). data is the engine PrintTemplate JSON, parsed client-side. ──
  listTemplates: () => request<PrintTemplateRow[]>('/me/templates'),
  saveTemplate: (name: string, data: Record<string, unknown>) =>
    request<PrintTemplateRow>('/me/templates', { method: 'POST', body: { name, data } }),
  deleteTemplate: (id: string) =>
    request<void>(`/me/templates/${id}`, { method: 'DELETE' }),

  // ── E2 billing ──
  // The plan flip happens asynchronously via the verified webhook, so checkout
  // only returns a redirect URL; the SPA shows pending→active honestly afterwards.
  getBillingConfig: () => request<BillingConfig>('/billing/config'),
  getSubscription: () => request<Subscription>('/billing/subscription'),
  startCheckout: (plan: string, interval: BillingInterval) =>
    request<{ url: string }>('/billing/checkout', { method: 'POST', body: { plan, interval } }),
  openBillingPortal: () => request<{ url: string }>('/billing/portal', { method: 'POST' }),
  // 7-day money-back guarantee: full refund + cancel for a self-serve subscription.
  // Server-enforced (window + eligibility); the downgrade lands via the webhook.
  requestRefund: () =>
    request<{ status: string; refunded: boolean }>('/billing/refund', { method: 'POST' }),

  // ── admin (server enforces is_admin; non-admins get 404 on all of these) ──
  adminListPlans: () => request<{ plans: AdminPlan[] }>('/admin/plans'),
  adminUpdatePlan: (planId: string, patch: Record<string, PlanLimitValue>) =>
    request<AdminPlan>(`/admin/plans/${planId}`, { method: 'PATCH', body: patch }),
  // Both growth series in one round trip, on one shared day spine.
  adminGrowth: (from: string, to: string) =>
    request<AdminGrowth>('/admin/stats/growth', { query: { from, to } }),
  adminSearchUsers: (query: string) =>
    request<{ items: AdminUserRow[]; next_cursor: string | null }>('/admin/users', {
      query: { query: query || undefined },
    }),
  adminChangeUserPlan: (userId: string, planId: string) =>
    request<{ status: string; plan: string }>(`/admin/users/${userId}/subscription`, {
      method: 'POST',
      body: { plan_id: planId },
    }),

  // ── Teams & seats — customer Team Admin/Owner self-serve (routers/teams.py) ──
  // Adding a member who is over the seat cap throws a LimitExceededError (402,
  // metric "max_seats"); the caller surfaces an inline "all seats in use" message.
  listTeams: () => request<{ teams: TeamSummary[] }>('/teams'),
  getTeam: (teamId: string) => request<TeamDetail>(`/teams/${teamId}`),
  addTeamMember: (teamId: string, email: string, role: AssignableRole) =>
    request<TeamMemberRow>(`/teams/${teamId}/members`, { method: 'POST', body: { email, role } }),
  setTeamMemberRole: (teamId: string, userId: string, role: AssignableRole) =>
    request<TeamMemberRow>(`/teams/${teamId}/members/${userId}`, {
      method: 'PATCH',
      body: { role },
    }),
  removeTeamMember: (teamId: string, userId: string) =>
    request<{ status: string }>(`/teams/${teamId}/members/${userId}`, { method: 'DELETE' }),

  // ── Teams — Platform Admin operator surface (routers/admin.py; 404s non-admins) ──
  adminListTeams: () => request<{ teams: AdminTeamRow[] }>('/admin/teams'),
  adminCreateTeam: (body: {
    name: string;
    plan_id: string;
    owner_email: string;
    seats: number | null;
  }) => request<AdminTeamRow>('/admin/teams', { method: 'POST', body }),
  adminGetTeam: (teamId: string) => request<AdminTeamDetail>(`/admin/teams/${teamId}`),
  adminUpdateTeam: (
    teamId: string,
    body: { name?: string; plan_id?: string; seats?: number | null },
  ) => request<AdminTeamRow>(`/admin/teams/${teamId}`, { method: 'PATCH', body }),
  adminAddTeamMember: (teamId: string, email: string, role: AssignableRole) =>
    request<TeamMemberRow>(`/admin/teams/${teamId}/members`, {
      method: 'POST',
      body: { email, role },
    }),
  adminSetTeamMemberRole: (teamId: string, userId: string, role: AssignableRole) =>
    request<TeamMemberRow>(`/admin/teams/${teamId}/members/${userId}`, {
      method: 'PATCH',
      body: { role },
    }),
  adminRemoveTeamMember: (teamId: string, userId: string) =>
    request<{ status: string }>(`/admin/teams/${teamId}/members/${userId}`, { method: 'DELETE' }),

  // C0-R: record the last-opened model (editor-first landing). 204; fire-and-forget.
  setLastModel: (modelId: string) =>
    request<void>('/me/last-model', { method: 'POST', body: { model_id: modelId } }),

  // preferences (C1; server-authoritative settings + theming)
  getPreferences: () => request<PreferencesOut>('/me/preferences'),
  putPreferences: (prefs: Preferences) =>
    request<PreferencesOut>('/me/preferences', { method: 'PUT', body: prefs }),

  // doc blob (REST persistence adapter — increment 6)
  loadDoc: loadDocBlob,
  saveDoc: saveDocBlob,

  // local-first sync (C2): a short-lived ticket for the y-websocket sidecar
  getSyncTicket: (modelId: string) =>
    request<SyncTicketResponse>(`/models/${modelId}/sync-ticket`, { method: 'POST' }),

  // ── D1 share links — owner-side management (authed) ──
  listShareLinks: (modelId: string) =>
    request<ShareLinkSummary[]>(`/models/${modelId}/share-links`),
  createShareLink: (modelId: string, scope: ShareScope) =>
    request<ShareLink>(`/models/${modelId}/share-links`, { method: 'POST', query: { scope } }),
  revokeShareLink: (modelId: string, linkId: string) =>
    request<{ status: string }>(`/models/${modelId}/share-links/${linkId}`, { method: 'DELETE' }),
  // Create a scoped link and EMAIL its /s/{token} URL to one recipient (all tiers).
  // The token rides the email; nothing sensitive comes back here.
  emailShareLink: (modelId: string, email: string, scope: ShareScope) =>
    request<{ status: string }>(`/models/${modelId}/share-links/email`, {
      method: 'POST',
      body: { email, scope },
    }),

  // ── D-series: redeem an EDIT-scope link as a signed-in collaborator (authed) ──
  joinShare: (token: string) =>
    request<ShareJoin>(`/share/${encodeURIComponent(token)}/join`, { method: 'POST' }),

  // ── D1 share links — PUBLIC anonymous access (no account, no CSRF) ──
  shareMeta: (token: string) =>
    publicRequest<ShareMeta>(`/share/${encodeURIComponent(token)}/meta`),
  // anonId echoed on refreshes so a refresh isn't counted as a new open.
  shareAccess: (token: string, anonId?: string) =>
    publicRequest<ShareAccess>(`/share/${encodeURIComponent(token)}/access`, {
      method: 'POST',
      body: anonId ? { anon_id: anonId } : {},
    }),

  // ── D2 comments — authed members (/api/v1/models/{id}/comments) ──
  listComments: (modelId: string, resolved: CommentResolvedFilter = 'open') =>
    request<Comment[]>(`/models/${modelId}/comments`, { query: { resolved } }),
  createComment: (modelId: string, body: CommentCreateIn) =>
    request<Comment>(`/models/${modelId}/comments`, { method: 'POST', body }),
  patchComment: (modelId: string, commentId: string, body: CommentPatchIn) =>
    request<Comment>(`/models/${modelId}/comments/${commentId}`, { method: 'PATCH', body }),
  deleteComment: (modelId: string, commentId: string) =>
    request<{ status: string }>(`/models/${modelId}/comments/${commentId}`, { method: 'DELETE' }),

  // ── D2 comments — PUBLIC anonymous guests on a comment-scope link ──
  listShareComments: (token: string, anonId?: string, resolved: CommentResolvedFilter = 'open') =>
    publicRequest<Comment[]>(`/share/${encodeURIComponent(token)}/comments`, {
      query: { anon_id: anonId, resolved },
    }),
  createShareComment: (token: string, body: ShareCommentCreateIn) =>
    publicRequest<Comment>(`/share/${encodeURIComponent(token)}/comments`, { method: 'POST', body }),
  patchShareComment: (token: string, commentId: string, body: ShareCommentPatchIn) =>
    publicRequest<Comment>(`/share/${encodeURIComponent(token)}/comments/${commentId}`, {
      method: 'PATCH',
      body,
    }),
  deleteShareComment: (token: string, commentId: string, anonId: string) =>
    publicRequest<{ status: string }>(
      `/share/${encodeURIComponent(token)}/comments/${commentId}`,
      { method: 'DELETE', query: { anon_id: anonId } },
    ),

  // ── D3 version control — authed (model:read to view/compare, model:write to mutate) ──
  // Default: only deliberate versions (named/restore/merge). include_auto surfaces
  // the auto recovery checkpoints (the collapsed "auto-saves" sub-view).
  listVersions: (modelId: string, opts?: { branch?: string; includeAuto?: boolean }) =>
    request<Version[]>(`/models/${modelId}/versions`, {
      query: { branch: opts?.branch, include_auto: opts?.includeAuto ? 'true' : undefined },
    }),
  nameVersion: (modelId: string, label: string, doc?: string) =>
    request<Version>(`/models/${modelId}/versions`, {
      method: 'POST',
      body: doc !== undefined ? { label, doc } : { label },
    }),
  restoreVersion: (modelId: string, snapshotId: string) =>
    request<Version>(`/models/${modelId}/restore`, {
      method: 'POST',
      body: { snapshot_id: snapshotId },
    }),
  listBranches: (modelId: string) => request<Branch[]>(`/models/${modelId}/branches`),
  // The version DAG (deliberate versions + branch heads, auto-contracted parent
  // edges) for the history panel's git-style branch tree.
  getGraph: (modelId: string) => request<VersionGraph>(`/models/${modelId}/graph`),
  createBranch: (modelId: string, name: string, fromSnapshotId: string) =>
    request<Branch>(`/models/${modelId}/branches`, {
      method: 'POST',
      body: { name, from_snapshot_id: fromSnapshotId },
    }),
  patchBranch: (branchId: string, body: { name?: string; archived?: boolean }) =>
    request<Branch>(`/branches/${branchId}`, { method: 'PATCH', body }),
  switchBranch: (modelId: string, branchId: string) =>
    request<Branch>(`/models/${modelId}/switch-branch`, {
      method: 'POST',
      body: { branch_id: branchId },
    }),
  snapshotDoc: snapshotDocBlob,
  getDiff: (modelId: string, from: string, to: string) =>
    request<DiffBlobs>(`/models/${modelId}/diff`, { query: { from, to } }),
  // FIX_01 assisted merge: preview (the 3 docs for threeWayDiff) + commit (the
  // client-built merged doc, landed as a named version on the current branch).
  mergePreview: (modelId: string, fromBranch: string, intoBranch?: string) =>
    request<MergePreview>(`/models/${modelId}/merge-preview`, {
      query: { from: fromBranch, into: intoBranch },
    }),
  mergeBranch: (modelId: string, fromBranch: string, doc: string, label: string) =>
    request<Version>(`/models/${modelId}/merge`, {
      method: 'POST',
      body: { from_branch: fromBranch, doc, label },
    }),
  listModelAudit: (modelId: string, opts?: { action?: string; before?: string }) =>
    request<AuditEvent[]>(`/models/${modelId}/audit`, {
      query: { action: opts?.action, before: opts?.before },
    }),
};
